import { useState } from "react";

import { GitBranch } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogFooterWithShortcuts, CancelButton, SubmitButton } from "@/components/ui/dialog-footer-with-shortcuts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface BranchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBranch: (branchName: string) => void;
  messageContent: string;
}

export function BranchDialog({ 
  open, 
  onOpenChange, 
  onBranch,
  messageContent 
}: BranchDialogProps) {
  const [branchName, setBranchName] = useState("");

  const handleBranch = () => {
    if (branchName.trim()) {
      onBranch(branchName.trim());
      setBranchName("");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[500px]"
        onSubmit={handleBranch}
        submitDisabled={!branchName.trim()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-primary" />
            Create New Branch
          </DialogTitle>
          <DialogDescription>
            This will create a new conversation branch from this point. All previous context
            and memories will be inherited, but new changes will only affect this branch.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="branch-name">Branch Name</Label>
            <Input
              id="branch-name"
              placeholder="e.g., Explore alternative approach"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleBranch();
                }
              }}
              autoFocus
            />
          </div>
          <div className="rounded-md bg-muted p-3">
            <p className="text-sm text-muted-foreground mb-1 font-medium">
              Branching from:
            </p>
            <p className="text-sm line-clamp-3">{messageContent}</p>
          </div>
        </div>
        <DialogFooterWithShortcuts onSubmit={handleBranch}>
          <CancelButton onClick={() => onOpenChange(false)}>
            Cancel
          </CancelButton>
          <SubmitButton onClick={handleBranch} disabled={!branchName.trim()}>
            <GitBranch className="h-4 w-4 mr-2" />
            Create Branch
          </SubmitButton>
        </DialogFooterWithShortcuts>
      </DialogContent>
    </Dialog>
  );
}
